import { useCallback, useState } from 'react'

import { BaselineFacts } from '../../components/baseline/BaselineFacts'
import { PageHeader } from '../../components/PageHeader'
import {
  Card,
  ConditionTiles,
  DataTable,
  EmptyState,
  Notice,
  ProblemNotice,
  SegmentedControl,
  type Column,
} from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import {
  getBaselineStatus,
  getBaselineVector,
  listRecordings,
  type BaselineStatus,
  type BaselineVector,
  type Condition,
  type RecordingRecord,
} from '../../lib/api'
import { DEVICE_IDS, DEVICE_LABELS, baselineDevice, recordedDevices, type DeviceId } from '../../lib/devices'
import { isNotFound } from '../../lib/problem'
import { usePagedList, type Page } from '../../lib/usePagedList'
import { DEFAULT_MAX_PAGES } from '../../lib/usePickFromPages'
import { useRetryLanding, type ArmLanding } from '../../lib/useRetryLanding'
import { useResource } from '../../lib/useResource'

/**
 * Baseline — what your own history amounts to, for one condition.
 *
 * The three facts, and why nothing is derived from them: see
 * `components/baseline/BaselineFacts.tsx`, which now renders them.
 *
 * ── THE MEDIAN/MAD VECTOR IS NOT AN INTERVAL ────────────────────────────────
 *
 * §5.2: `median` and `mad` are a reference point and a spread. They are **not**
 * a supplied lower and upper bound. Turning them into `median ± 2·mad`, a
 * normal/abnormal range, or a robust-z is arithmetic that belongs to
 * `eeg-tracking`, where it is checked — and doing it here would be the client
 * making a scientific claim, which acceptance criterion 2 forbids. The table
 * below prints two numbers per feature and draws no conclusion from either.
 *
 * ── AND THE CONDITION IS CHOSEN, NOT ASSUMED ────────────────────────────────
 *
 * §5.1 asks for "an explicitly selected condition". EC and EO are separate
 * baselines; a screen that quietly defaulted to EC would label one baseline with
 * the other's name for anyone who never touched the control.
 *
 * ── AND EVERY HEADBAND KEEPS ITS OWN (Sprint 1.9.2 S4) ──────────────────────
 *
 * The eight-channel BrainBit Pro and the four-channel BrainBit are different
 * analysis cohorts with different feature sets, so a user who has recorded with
 * both has two baselines per condition. Both endpoints take `device_id`, and
 * omitting it reads the Pro. This screen always SAYS which headband's baseline
 * it is showing, and offers a choice only to someone whose recordings came from
 * both — see `baselineDevice` for which one it opens on.
 */
export function Baseline() {
  const { account } = useAuth()
  const userId = account?.user_id ?? null
  const [condition, setCondition] = useState<Condition | null>(null)

  // WHICH HEADBANDS THIS USER HAS RECORDED WITH, read off their recordings.
  // `RecordingRecord.device_id` is the profile the backend resolved from each
  // archive. Not inferred from the baselines themselves: a backend that
  // predates `device_id` on these routes ignores the parameter and answers
  // with the Pro baseline, so "the classic baseline has sessions" could be the
  // Pro's own history under the other headband's name. A classic RECORDING
  // exists only where the backend admits the classic device, which is the same
  // release that reads its baseline.
  const fetchPage = useCallback(
    (cursor: string | null, signal: AbortSignal): Promise<Page<RecordingRecord>> =>
      userId === null
        ? Promise.resolve({ items: [], next_cursor: null })
        : listRecordings(userId, { cursor }, { signal }),
    [userId],
  )
  const list = usePagedList<RecordingRecord>(fetchPage, { autoLoadPages: DEVICE_SCAN_PAGES })
  const recorded = recordedDevices(list.items)
  const [chosen, setChosen] = useState<DeviceId | null>(null)
  const device = baselineDevice(list.items, recorded, chosen)
  // Nothing is read until the first page has said which headband to read,
  // so a classic-only user never sees the Pro's empty baseline first.
  const deviceKnown = !list.isLoading

  const loadStatus = useCallback(
    (signal: AbortSignal): Promise<BaselineStatus | null> =>
      userId === null || condition === null || !deviceKnown
        ? Promise.resolve(null)
        : getBaselineStatus(userId, condition, { signal, deviceId: device }),
    [userId, condition, deviceKnown, device],
  )
  const loadVector = useCallback(
    (signal: AbortSignal): Promise<BaselineVector | null> =>
      userId === null || condition === null || !deviceKnown
        ? Promise.resolve(null)
        : getBaselineVector(userId, condition, 'full', { signal, deviceId: device }),
    [userId, condition, deviceKnown, device],
  )

  const status = useResource(loadStatus)
  const vector = useResource(loadVector)
  /**
   * One landing per section, because each has its own retry and its own notice.
   *
   * The notice unmounts when the error clears and takes the focused "Try again"
   * with it, which is the case `Button.tsx:67-69` names as uncoverable. The
   * wrapper below is a plain `<div>` inside `.stack`, a flex column with a gap
   * (product.css:31-36) -- so it becomes the flex item and the spacing is
   * unchanged, which wrapping BOTH sections in one div would not be.
   *
   * A 404 renders `NoHistory` rather than a notice, so no retry ever had focus
   * and `armed` stays false: the effect does nothing on that path.
   */
  const [factsLandOn, armFacts] = useRetryLanding(status.error != null)
  const [vectorLandOn, armVector] = useRetryLanding(vector.error != null)
  const [deviceLandOn, armDevice] = useRetryLanding(list.error != null)

  return (
    <div className="stack stack--standard">
      {/* The eyebrow repeats the `<h1>` verbatim, so it reaches the DOM
          `aria-hidden` (R25): it is a kicker, not a second announcement of the
          page's name. */}
      <PageHeader
        eyebrow="Baseline"
        eyebrowHidden
        title="Baseline"
        lead="What your own recordings add up to. Eyes closed and eyes open are separate baselines and are never combined."
      />

      <div className="grid-row baseline__head">
        <Card eyebrow="Condition" className="span-6">
          <div className="baseline__condition">
            <ConditionTiles label="Condition" hideLabel value={condition} onChange={setCondition} />
          </div>
        </Card>

        {/* Always mounted, so the landing exists at the moment a retry succeeds. */}
        <div className="retry-landing baseline__device-landing span-6" ref={deviceLandOn} tabIndex={-1}>
          {deviceKnown && (
            <HeadbandCard recorded={recorded} device={device} onChange={setChosen} />
          )}
        </div>
      </div>

      {list.error != null && (
        <ProblemNotice error={list.error} onRetry={list.reload} landing={armDevice}>
          Your recordings could not be loaded, so this may not show every headband you have
          recorded with.
        </ProblemNotice>
      )}

      {condition === null ? (
        <Card>
          <EmptyState title="Choose a condition">
            <p className="product-empty__body">
              A baseline belongs to one condition. Pick eyes closed or eyes open above and
              this screen will show what that one contains.
            </p>
          </EmptyState>
        </Card>
      ) : (
        <>
          <div className="retry-landing" ref={factsLandOn} tabIndex={-1}>
            <BaselineFacts
              condition={condition}
              status={status.data}
              isLoading={status.isLoading || !deviceKnown}
              error={status.error}
              onRetry={status.reload}
              landing={armFacts}
            />
          </div>
          <div className="retry-landing baseline__reference-landing" ref={vectorLandOn} tabIndex={-1}>
            <PersonalReference
              vector={vector.data}
              isLoading={vector.isLoading || !deviceKnown}
              error={vector.error}
              onRetry={vector.reload}
              landing={armVector}
            />
          </div>
        </>
      )}

      {/* Light remains a standard card; the scoped dark rule restores the flat surface.
          The canvas draws `.card.card--flat` here (1129).
          This is the one flat card in the app that would sit on the BARE FIELD
          rather than inside another card, and `--fill-soft` over `--bg-base`
          puts the muted eyebrow at 4.46:1 in the light theme -- under the
          4.5:1 `tokens.test.ts` requires of normal text, and measured here
          rather than assumed (RULINGS Corrections → R25 records the same
          number). `--muted` is not one of the three tokens S1b was licensed to
          deepen, so the surface gives way and the 78ch paragraph type below
          does not. Recorded gate-forced. */}
      <Card eyebrow="What this means" className="baseline__meaning">
        <p className="baseline__explain">
          Every number on this screen is compared with <strong>your own history</strong>,
          never with anyone else&rsquo;s. The median is the middle of your past recordings
          for a feature — the value you most typically produce. The MAD is how much your
          own readings usually vary around that middle.
        </p>
        <p className="baseline__explain">
          They are a reference point and a spread. They are not a normal range, not a
          target, and not a threshold: a value far from your median means today differed
          from your usual, and nothing more than that.
        </p>
      </Card>
    </div>
  )
}

/**
 * How many pages of recordings to read for the headbands they came from.
 *
 * The same bound `pickFromPages` documents — five pages is 250 recordings at
 * the default page size. A user whose other headband is older than that is not
 * offered it here; what is SHOWN is still named correctly, because the name
 * comes from the device actually requested, never from the scan.
 */
const DEVICE_SCAN_PAGES = DEFAULT_MAX_PAGES

/**
 * Which headband's baseline this is — ALWAYS said, and chosen only by someone
 * who has recorded with both.
 *
 * One headband (or none yet): its name and contact count, and no control. Two:
 * a choose-one group over both, never a merged view — the two baselines are
 * built from different electrodes and are not one history.
 */
function HeadbandCard({
  recorded,
  device,
  onChange,
}: {
  recorded: readonly DeviceId[]
  device: DeviceId
  onChange: (device: DeviceId) => void
}) {
  // "Both", asked as a membership question rather than a count against a
  // literal: nothing on this screen compares a number.
  const both = DEVICE_IDS.every((id) => recorded.includes(id))
  if (!both) {
    return (
      <Card eyebrow="Headband">
        <p className="baseline__device">{DEVICE_LABELS[device]}</p>
      </Card>
    )
  }
  return (
    <Card eyebrow="Headband">
      <div className="baseline__device-pick">
        <SegmentedControl
          label="Headband"
          hideLabel
          options={recorded.map((id) => ({ value: id, label: DEVICE_LABELS[id] }))}
          value={device}
          onChange={onChange}
        />
      </div>
      <p className="baseline__note">
        You have recorded with both headbands. Each keeps its own baseline, and the two are
        never combined.
      </p>
    </Card>
  )
}

function PersonalReference({
  vector,
  isLoading,
  error,
  onRetry,
  landing,
}: {
  vector: BaselineVector | null
  isLoading: boolean
  error: unknown
  onRetry: () => void
  landing?: ArmLanding
}) {
  if (isLoading) {
    return (
      <Card>
        <p className="loading-row" role="status">
          <span className="spinner" aria-hidden="true" /> Loading your personal reference…
        </p>
      </Card>
    )
  }

  if (error != null) {
    if (isNotFound(error)) return null
    return (
      <ProblemNotice error={error} onRetry={onRetry} landing={landing}>
        Your personal reference could not be loaded.
      </ProblemNotice>
    )
  }

  if (vector === null) return null

  // The UNION, not `median`'s keys. The two maps come from
  // `compute_baseline_stats` as separate series and nothing in the contract says
  // they carry the same features. Keying on one of them drops whatever the other
  // knows about, silently -- and a feature with a spread but no median is
  // exactly the kind of partial answer this screen is supposed to show rather
  // than tidy away.
  const features = [...new Set([...Object.keys(vector.median), ...Object.keys(vector.mad)])].sort()

  if (features.length === 0) {
    return (
      <Card eyebrow="Personal reference">
        <p className="product-empty__body">
          There is no per-feature reference for this condition yet. It appears once enough
          recordings have been scored for one to be computed.
        </p>
      </Card>
    )
  }

  const rows = features.map((feature) => ({
    feature,
    median: vector.median[feature],
    // A feature present in `median` and absent from `mad` is shown as absent, not
    // as zero -- the same rule the score cards keep. Zero spread and unknown
    // spread are different statements.
    mad: vector.mad[feature],
  }))

  const columns: Column<(typeof rows)[number]>[] = [
    {
      key: 'feature',
      header: 'Feature',
      /**
       * The plain name ABOVE and the raw key BENEATH it (ask B6). Baseline was
       * the one screen showing identifiers -- `alpha_rel_posterior`,
       * `fooof_exponent_mean` -- where every other shows sentences the backend
       * wrote.
       *
       * The key does not go away when a name arrives. It is what a person
       * quotes in a support thread and what the archive actually calls the
       * column, so hiding it would trade one kind of opacity for another; and
       * it is the fallback when the payload names nothing, which is legitimate
       * -- 248 of the archive's 274 features are per-channel, and a name for a
       * feature the package has never heard of would have to be invented here.
       */
      render: (r) => {
        const title = vector.display?.[r.feature]?.title
        if (title === undefined) return <span className="product-id">{r.feature}</span>
        return (
          <>
            <span className="feature-name">{title}</span>
            <span className="product-id">{r.feature}</span>
          </>
        )
      },
    },
    {
      key: 'median',
      // The payload's word for the column (ask B6), falling back to the one
      // the client used to author. Same text today -- which is the point: the
      // client stops OWNING it without the reader seeing a change.
      header: vector.median_label ?? 'Median',
      // Alignment belongs to the COLUMN, so "Not available" travels with the
      // numbers rather than needing a per-cell override `DataTable` does not
      // have. The card rendering ignores `align` and right-aligns every value
      // through `.record__value` already.
      align: 'end',
      render: (r) =>
        r.median === undefined ? (
          <span>Not available</span>
        ) : (
          <span className="mono-cell">{r.median}</span>
        ),
    },
    {
      key: 'mad',
      header: vector.mad_label ?? 'MAD',
      align: 'end',
      render: (r) =>
        r.mad === undefined ? <span>Not available</span> : <span className="mono-cell">{r.mad}</span>,
    },
  ]

  return (
    <Card
      eyebrow="Personal reference"
      // ONE STRING through `meta`, which span-wraps it in `.card__meta`. One
      // string and not three nodes: `Baseline.test.tsx`'s "shows a missing MAD
      // as unavailable, never as zero" asserts no element on the screen has the
      // bare text "0", and a count rendered as its own node would be exactly
      // that the day a reference has none.
      //
      // `features.length` is a read and `=== 1` a pluralisation, neither of
      // which is a `clientScience` site: its `OPS` and `CMP` sets inventory
      // `+ - * / %`, their compound forms and `< > <= >=`
      // (`clientScience.test.ts:239-252`), and `Math.*` separately (`:294-297`).
      // So this needs no ALLOWED row -- and a row matching no site would itself
      // redden "an allowlist entry no longer matches any source" (`:589-593`).
      // It counts payload KEYS; nothing is compared with it.
      meta={`${features.length} ${features.length === 1 ? 'feature' : 'features'} · median and MAD · your own history only`}
    >
      <div className="baseline__body">
        <DataTable
          caption="Your per-feature median and MAD"
          rows={rows}
          columns={columns}
          rowKey={(r) => r.feature}
          empty={<p>No per-feature reference for this condition yet.</p>}
        />
        {/* `BaselineVector.low_confidence_flag` is a SECOND payload flag and may
            legitimately disagree with the status one, so this stays a live
            `Notice` rather than becoming quiet prose. */}
        {vector.low_confidence_flag && (
          <Notice tone="warn">
            These values are based on a short history and will move as more recordings are
            scored.
          </Notice>
        )}
      </div>
    </Card>
  )
}
